/**
 * SearchBar Component
 * Enhanced search input with autocomplete, recent searches, and saved searches
 * Features:
 * - Real-time search with debounce
 * - Recent searches history (stored in localStorage)
 * - Saved searches management
 * - Keyboard navigation support
 * - Visual search suggestions
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Search,
  X,
  Clock,
  Star,
  StarOff,
  Trash2,
  ArrowRight,
  FileText,
  Hash,
  Image,
  Table,
  Mail,
  Presentation,
} from 'lucide-react'
import { useContentQuickSearch } from '@/lib/hooks/use-content-hub'
import type { ContentItem, ContentItemType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface SearchBarProps {
  onSearch?: (query: string) => void
  onSelectItem?: (item: ContentItem) => void
  placeholder?: string
  className?: string
  autoFocus?: boolean
  showFullSearch?: boolean
}

interface SavedSearch {
  id: string
  query: string
  createdAt: string
}

// ============================================
// Constants
// ============================================

const RECENT_SEARCHES_KEY = 'content-hub-recent-searches'
const SAVED_SEARCHES_KEY = 'content-hub-saved-searches'
const MAX_RECENT_SEARCHES = 10
const MAX_SUGGESTIONS = 8

const CONTENT_TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: Image,
  Table: Table,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const CONTENT_TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'text-blue-500',
  DataPoint: 'text-green-500',
  Image: 'text-purple-500',
  Table: 'text-amber-500',
  EmailSnippet: 'text-rose-500',
  SlideElement: 'text-orange-500',
}

// ============================================
// Hooks
// ============================================

function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  const addRecentSearch = useCallback((query: string) => {
    if (!query.trim()) return

    setRecentSearches((prev) => {
      const filtered = prev.filter((q) => q.toLowerCase() !== query.toLowerCase())
      const updated = [query.trim(), ...filtered].slice(0, MAX_RECENT_SEARCHES)
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const removeRecentSearch = useCallback((query: string) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((q) => q !== query)
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([])
    localStorage.removeItem(RECENT_SEARCHES_KEY)
  }, [])

  return { recentSearches, addRecentSearch, removeRecentSearch, clearRecentSearches }
}

function useSavedSearches() {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(() => {
    try {
      const saved = localStorage.getItem(SAVED_SEARCHES_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  const saveSearch = useCallback((query: string) => {
    if (!query.trim()) return

    setSavedSearches((prev) => {
      // Don't add duplicates
      if (prev.some((s) => s.query.toLowerCase() === query.toLowerCase())) {
        return prev
      }

      const newSearch: SavedSearch = {
        id: crypto.randomUUID(),
        query: query.trim(),
        createdAt: new Date().toISOString(),
      }
      const updated = [newSearch, ...prev]
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const removeSavedSearch = useCallback((id: string) => {
    setSavedSearches((prev) => {
      const updated = prev.filter((s) => s.id !== id)
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const isSaved = useCallback(
    (query: string) => {
      return savedSearches.some((s) => s.query.toLowerCase() === query.toLowerCase())
    },
    [savedSearches]
  )

  return { savedSearches, saveSearch, removeSavedSearch, isSaved }
}

// ============================================
// SearchBar Component
// ============================================

export function SearchBar({
  onSearch,
  onSelectItem,
  placeholder = 'Search content...',
  className,
  autoFocus = false,
  showFullSearch = true,
}: SearchBarProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)

  // Hooks
  const { recentSearches, addRecentSearch, removeRecentSearch, clearRecentSearches } =
    useRecentSearches()
  const { savedSearches, saveSearch, removeSavedSearch, isSaved } = useSavedSearches()

  // Quick search with debounce
  const { data: searchResults = [], isLoading } = useContentQuickSearch(query, MAX_SUGGESTIONS, {
    debounceMs: 300,
  })

  // Combine all selectable items for keyboard navigation
  const allItems = useMemo(() => {
    const items: Array<{ type: 'result' | 'recent' | 'saved'; value: string | ContentItem }> = []

    // Add search results
    searchResults.forEach((item) => {
      items.push({ type: 'result', value: item })
    })

    // Add recent searches if no query
    if (!query.trim()) {
      recentSearches.slice(0, 5).forEach((q) => {
        items.push({ type: 'recent', value: q })
      })
      savedSearches.slice(0, 5).forEach((s) => {
        items.push({ type: 'saved', value: s.query })
      })
    }

    return items
  }, [searchResults, recentSearches, savedSearches, query])

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setSelectedIndex(-1)
    setIsOpen(true)
  }, [])

  // Handle search submit
  const handleSearch = useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim()) return

      addRecentSearch(searchQuery)
      setIsOpen(false)

      if (onSearch) {
        onSearch(searchQuery)
      } else if (showFullSearch) {
        navigate(`/content-hub/search?q=${encodeURIComponent(searchQuery)}`)
      }
    },
    [addRecentSearch, onSearch, showFullSearch, navigate]
  )

  // Handle item selection
  const handleSelectItem = useCallback(
    (item: ContentItem) => {
      setIsOpen(false)
      if (onSelectItem) {
        onSelectItem(item)
      } else {
        navigate(`/content-hub/item/${item.id}`)
      }
    },
    [onSelectItem, navigate]
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setIsOpen(true)
          e.preventDefault()
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < allItems.length) {
            const item = allItems[selectedIndex]
            if (item?.type === 'result') {
              handleSelectItem(item.value as ContentItem)
            } else if (item) {
              handleSearch(item.value as string)
            }
          } else {
            handleSearch(query)
          }
          break
        case 'Escape':
          setIsOpen(false)
          inputRef.current?.blur()
          break
      }
    },
    [isOpen, allItems, selectedIndex, query, handleSearch, handleSelectItem]
  )

  // Toggle saved search
  const toggleSaved = useCallback(
    (searchQuery: string) => {
      if (isSaved(searchQuery)) {
        const saved = savedSearches.find(
          (s) => s.query.toLowerCase() === searchQuery.toLowerCase()
        )
        if (saved) {
          removeSavedSearch(saved.id)
        }
      } else {
        saveSearch(searchQuery)
      }
    },
    [isSaved, savedSearches, removeSavedSearch, saveSearch]
  )

  // Clear input
  const handleClear = useCallback(() => {
    setQuery('')
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }, [])

  // Show popover content
  const showPopover = isOpen && (!!query.trim() || recentSearches.length > 0 || savedSearches.length > 0)

  return (
    <Popover open={showPopover} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={cn('relative', className)}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className={cn(
              'h-10 pl-9 pr-9 rounded-xl',
              'bg-white dark:bg-neutral-900',
              'border-neutral-200 dark:border-neutral-700',
              'focus:border-blue-500 dark:focus:border-blue-500'
            )}
          />
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[400px] p-0 rounded-xl shadow-lg"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ScrollArea className="max-h-[400px]">
          {/* Search Results */}
          {query.trim() && searchResults.length > 0 && (
            <div className="p-2">
              <p className="px-2 py-1.5 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Results
              </p>
              {searchResults.map((item, index) => {
                const Icon = CONTENT_TYPE_ICONS[item.type]
                const colorClass = CONTENT_TYPE_COLORS[item.type]
                const isSelected = selectedIndex === index

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelectItem(item)}
                    className={cn(
                      'w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left',
                      'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                    )}
                  >
                    <div className={cn('p-1.5 rounded-lg bg-neutral-100 dark:bg-neutral-800', colorClass)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                        {item.name}
                      </p>
                      {item.description && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-neutral-400" />
                  </button>
                )
              })}

              {showFullSearch && (
                <>
                  <Separator className="my-2" />
                  <button
                    type="button"
                    onClick={() => handleSearch(query)}
                    className={cn(
                      'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg',
                      'text-sm font-medium text-blue-600 dark:text-blue-400',
                      'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    )}
                  >
                    <Search className="h-4 w-4" />
                    Search for "{query}"
                  </button>
                </>
              )}
            </div>
          )}

          {/* Loading State */}
          {query.trim() && isLoading && (
            <div className="p-4 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-neutral-500">
                <div className="h-4 w-4 border-2 border-neutral-300 border-t-blue-500 rounded-full animate-spin" />
                Searching...
              </div>
            </div>
          )}

          {/* No Results */}
          {query.trim() && !isLoading && searchResults.length === 0 && (
            <div className="p-4">
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center">
                No results found for "{query}"
              </p>
              {showFullSearch && (
                <button
                  type="button"
                  onClick={() => handleSearch(query)}
                  className={cn(
                    'w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg',
                    'text-sm font-medium text-blue-600 dark:text-blue-400',
                    'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  )}
                >
                  <Search className="h-4 w-4" />
                  Search all content
                </button>
              )}
            </div>
          )}

          {/* Recent Searches */}
          {!query.trim() && recentSearches.length > 0 && (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  Recent Searches
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearRecentSearches}
                  className="h-6 text-xs text-neutral-400 hover:text-neutral-600"
                >
                  Clear
                </Button>
              </div>
              {recentSearches.slice(0, 5).map((searchQuery, index) => {
                const listIndex = searchResults.length + index
                const isSelected = selectedIndex === listIndex

                return (
                  <div
                    key={searchQuery}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-lg',
                      'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSearch(searchQuery)}
                      className="flex-1 flex items-center gap-2 text-left"
                    >
                      <Clock className="h-4 w-4 text-neutral-400" />
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">
                        {searchQuery}
                      </span>
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleSaved(searchQuery)}
                      className="h-6 w-6 text-neutral-400 hover:text-amber-500"
                    >
                      {isSaved(searchQuery) ? (
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                      ) : (
                        <StarOff className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRecentSearch(searchQuery)}
                      className="h-6 w-6 text-neutral-400 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Saved Searches */}
          {!query.trim() && savedSearches.length > 0 && (
            <>
              {recentSearches.length > 0 && <Separator />}
              <div className="p-2">
                <p className="px-2 py-1.5 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  Saved Searches
                </p>
                {savedSearches.slice(0, 5).map((saved, index) => {
                  const listIndex =
                    searchResults.length + Math.min(recentSearches.length, 5) + index
                  const isSelected = selectedIndex === listIndex

                  return (
                    <div
                      key={saved.id}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-lg',
                        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                        isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSearch(saved.query)}
                        className="flex-1 flex items-center gap-2 text-left"
                      >
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        <span className="text-sm text-neutral-700 dark:text-neutral-300">
                          {saved.query}
                        </span>
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSavedSearch(saved.id)}
                        className="h-6 w-6 text-neutral-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* Empty State */}
          {!query.trim() && recentSearches.length === 0 && savedSearches.length === 0 && (
            <div className="p-6 text-center">
              <Search className="h-8 w-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Start typing to search content
              </p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

export default SearchBar
